import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  FormErrorMessage,
  useToast,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import vendorApi from "../services/vendorApi";

const VendorFormModal = ({ isOpen, onClose, vendor, onSuccess }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    gstNumber: "",
    siteLocation: "",
    address: {
      street: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    },
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (vendor) {
      // Edit mode - populate form with vendor data
      setFormData({
        name: vendor.name || "",
        email: vendor.email || "",
        phone: vendor.phone || "",
        gstNumber: vendor.gstNumber || "",
        siteLocation: vendor.siteLocation || "",
        address: {
          street: vendor.address?.street || "",
          city: vendor.address?.city || "",
          state: vendor.address?.state || "",
          country: vendor.address?.country || "",
          postalCode: vendor.address?.postalCode || "",
        },
      });
    } else {
      // Create mode - reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        gstNumber: "",
        siteLocation: "",
        address: {
          street: "",
          city: "",
          state: "",
          country: "",
          postalCode: "",
        },
      });
    }
    setErrors({});
  }, [vendor, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleAddressChange = (field, value) => {
    setFormData({
      ...formData,
      address: { ...formData.address, [field]: value },
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation (min 2 characters)
    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Phone validation
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (vendor) {
        // Update existing vendor
        await vendorApi.updateVendor(vendor._id || vendor.id, formData);
        toast({
          title: "Vendor updated successfully",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        // Create new vendor
        await vendorApi.createVendor(formData);
        toast({
          title: "Vendor created successfully",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }

      onSuccess();
    } catch (error) {
      console.error("❌ Error saving vendor:", error);
      toast({
        title: vendor ? "Error updating vendor" : "Error creating vendor",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{vendor ? "Edit Vendor" : "Add New Vendor"}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Basic Information */}
              <FormControl isInvalid={errors.name} isRequired>
                <FormLabel>Vendor Name</FormLabel>
                <Input
                  placeholder="ABC Suppliers Ltd"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isInvalid={errors.email} isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      placeholder="contact@abcsuppliers.com"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isInvalid={errors.phone} isRequired>
                    <FormLabel>Phone</FormLabel>
                    <Input
                      placeholder="+91-9876543210"
                      value={formData.phone}
                      onChange={(e) =>
                        handleInputChange("phone", e.target.value)
                      }
                    />
                    <FormErrorMessage>{errors.phone}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>

              <FormControl>
                <FormLabel>GST Number</FormLabel>
                <Input
                  placeholder="27AABCU9603R1Z5"
                  value={formData.gstNumber}
                  onChange={(e) =>
                    handleInputChange("gstNumber", e.target.value)
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Site Location</FormLabel>
                <Input
                  placeholder="Plant 2 - Pune Site"
                  value={formData.siteLocation}
                  onChange={(e) =>
                    handleInputChange("siteLocation", e.target.value)
                  }
                />
              </FormControl>

              {/* Address Section */}
              <FormControl>
                <FormLabel fontWeight="bold" mt={2}>
                  Address
                </FormLabel>
              </FormControl>

              <FormControl>
                <FormLabel>Street Address</FormLabel>
                <Input
                  placeholder="123 Industrial Area"
                  value={formData.address.street}
                  onChange={(e) =>
                    handleAddressChange("street", e.target.value)
                  }
                />
              </FormControl>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input
                      placeholder="Mumbai"
                      value={formData.address.city}
                      onChange={(e) =>
                        handleAddressChange("city", e.target.value)
                      }
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input
                      placeholder="Maharashtra"
                      value={formData.address.state}
                      onChange={(e) =>
                        handleAddressChange("state", e.target.value)
                      }
                    />
                  </FormControl>
                </GridItem>
              </Grid>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel>Country</FormLabel>
                    <Input
                      placeholder="India"
                      value={formData.address.country}
                      onChange={(e) =>
                        handleAddressChange("country", e.target.value)
                      }
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel>Postal Code</FormLabel>
                    <Input
                      placeholder="400001"
                      value={formData.address.postalCode}
                      onChange={(e) =>
                        handleAddressChange("postalCode", e.target.value)
                      }
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" type="submit" isLoading={loading}>
              {vendor ? "Update Vendor" : "Create Vendor"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default VendorFormModal;
